import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { 
  Droplets, 
  FlaskConical, 
  TestTube, 
  Microscope,
  DollarSign,
  TrendingUp,
  Package,
  CheckCircle2,
  Clock,
  Building2,
  AlertCircle,
  Loader2,
  Eye,
  RefreshCw,
  Percent,
  BarChart3,
  Save,
  Edit,
  X,
  FileText,
  Clipboard,
  Truck,
  Play,
  CheckCircle,
  Archive,
  Calendar,
  Phone,
  MapPin,
  ChevronRight,
  Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Pipeline stages for work orders
const STAGES = [
  { id: "intake", label: "Intake", icon: FileText, color: "bg-slate-500" },
  { id: "triage", label: "Triage", icon: Clipboard, color: "bg-orange-500" },
  { id: "dispatch", label: "Dispatch", icon: Truck, color: "bg-blue-500" },
  { id: "in_progress", label: "In Progress", icon: Play, color: "bg-purple-500" },
  { id: "completion", label: "Completion", icon: CheckCircle, color: "bg-green-500" },
  { id: "closed", label: "Closed", icon: Archive, color: "bg-slate-700" },
];

export default function AdminEnvironmentalTesting() {
  const { token } = useAuth();
  const [catalog, setCatalog] = useState({ tests: [], fees: [], markup_percentage: 44 });
  const [orders, setOrders] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pipeline");
  const [editingMarkup, setEditingMarkup] = useState(false);
  const [newMarkup, setNewMarkup] = useState(44);
  const [savingMarkup, setSavingMarkup] = useState(false);
  
  // Work Order creation dialog
  const [createWODialogOpen, setCreateWODialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [woForm, setWoForm] = useState({
    scheduled_date: "",
    scheduled_time: "",
    contact_name: "",
    contact_phone: "",
    access_instructions: "",
    special_requirements: ""
  });
  const [creatingWO, setCreatingWO] = useState(false);

  // Stage update dialog
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [selectedWO, setSelectedWO] = useState(null);
  const [newStage, setNewStage] = useState("");
  const [stageNotes, setStageNotes] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catalogRes, ordersRes, workOrdersRes, quotesRes, analyticsRes] = await Promise.all([
        fetch(`${API}/api/environmental-testing/catalog/admin`),
        fetch(`${API}/api/environmental-testing/orders?limit=100`),
        fetch(`${API}/api/environmental-testing/work-orders?limit=100`),
        fetch(`${API}/api/environmental-testing/quotes?limit=50`),
        fetch(`${API}/api/environmental-testing/analytics/summary`)
      ]);
      
      const catalogData = await catalogRes.json();
      const ordersData = await ordersRes.json();
      const workOrdersData = await workOrdersRes.json();
      const quotesData = await quotesRes.json();
      const analyticsData = await analyticsRes.json();
      
      setCatalog(catalogData);
      setOrders(ordersData.orders || []);
      setWorkOrders(workOrdersData.work_orders || []);
      setQuotes(quotesData.quotes || []);
      setAnalytics(analyticsData);
    } catch (err) {
      toast.error("Failed to load environmental testing data");
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/environmental-testing/orders/${orderId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, updated_by: "admin" })
      });
      if (res.ok) {
        toast.success(`Order ${orderId} updated to ${newStatus}`);
        fetchData();
      } else {
        toast.error("Failed to update order status");
      }
    } catch (err) {
      toast.error("Failed to update order status");
    }
  };

  const handleSaveMarkup = async () => {
    if (newMarkup < 0 || newMarkup > 500) {
      toast.error("Markup must be between 0% and 500%");
      return;
    }
    setSavingMarkup(true);
    try {
      const res = await fetch(`${API}/api/environmental-testing/pricing`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ markup_percentage: parseFloat(newMarkup) })
      });
      if (res.ok) {
        toast.success(`Markup updated to ${newMarkup}%`);
        setEditingMarkup(false);
        fetchData();
      } else {
        toast.error("Failed to update markup");
      }
    } catch (err) {
      toast.error("Failed to update markup");
    } finally {
      setSavingMarkup(false);
    }
  };

  const openCreateWODialog = (order) => {
    setSelectedOrder(order);
    setWoForm({
      scheduled_date: "",
      scheduled_time: "",
      contact_name: order.requested_by || "",
      contact_phone: "",
      access_instructions: "",
      special_requirements: ""
    });
    setCreateWODialogOpen(true);
  };

  const createWorkOrder = async () => {
    if (!woForm.scheduled_date || !woForm.contact_name || !woForm.contact_phone) {
      toast.error("Please fill in required fields");
      return;
    }
    setCreatingWO(true);
    try {
      const res = await fetch(`${API}/api/environmental-testing/work-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: selectedOrder.id,
          ...woForm
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Work Order ${data.work_order_id} created`);
        setCreateWODialogOpen(false);
        fetchData();
      } else {
        toast.error("Failed to create work order");
      }
    } catch (err) {
      toast.error("Failed to create work order");
    } finally {
      setCreatingWO(false);
    }
  };

  const openStageDialog = (wo, stage) => {
    setSelectedWO(wo);
    setNewStage(stage);
    setStageNotes("");
    setStageDialogOpen(true);
  };

  const updateWOStage = async () => {
    try {
      const res = await fetch(`${API}/api/environmental-testing/work-orders/${selectedWO.id}/stage?stage=${newStage}&updated_by=admin${stageNotes ? `&notes=${encodeURIComponent(stageNotes)}` : ""}`, {
        method: "PATCH"
      });
      if (res.ok) {
        toast.success(`Work order moved to ${newStage.replace("_", " ")}`);
        setStageDialogOpen(false);
        fetchData();
      } else {
        toast.error("Failed to update stage");
      }
    } catch (err) {
      toast.error("Failed to update stage");
    }
  };

  const markSamplesCollected = async (woId) => {
    try {
      const res = await fetch(`${API}/api/environmental-testing/work-orders/${woId}/samples-collected?collected_by=AB Labs Technician`, {
        method: "POST"
      });
      if (res.ok) {
        toast.success("Samples marked as collected");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const markResultsReady = async (woId) => {
    try {
      const res = await fetch(`${API}/api/environmental-testing/work-orders/${woId}/results-ready?results_summary=Results ready for review`, {
        method: "POST"
      });
      if (res.ok) {
        toast.success("Results marked as ready");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const completeWorkOrder = async (woId) => {
    try {
      const res = await fetch(`${API}/api/environmental-testing/work-orders/${woId}/complete?completed_by=admin`, {
        method: "POST"
      });
      if (res.ok) {
        toast.success("Work order completed");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to complete");
    }
  };

  // Group work orders by stage
  const workOrdersByStage = STAGES.reduce((acc, stage) => {
    acc[stage.id] = workOrders.filter(wo => wo.stage === stage.id);
    return acc;
  }, {});

  // Get orders that need work orders
  const ordersNeedingWO = orders.filter(o => !o.work_order_id && o.status !== "cancelled");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-environmental-testing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Droplets className="w-7 h-7 text-blue-500" />
            Environmental Testing Management
          </h1>
          <p className="text-slate-500 mt-1">Manage water testing orders, pricing, and AB Labs partnership</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Orders</p>
                  <p className="text-2xl font-bold">{analytics.financials?.order_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold">${(analytics.financials?.total_revenue || 0).toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Margin</p>
                  <p className="text-2xl font-bold">${(analytics.financials?.total_margin || 0).toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <Percent className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Markup</p>
                  <p className="text-2xl font-bold">{analytics.markup_percentage}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("pipeline")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === "pipeline" ? "border-blue-500 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid="pipeline-tab"
        >
          Pipeline ({workOrders.length})
        </button>
        <button
          onClick={() => setActiveTab("orders")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === "orders" ? "border-blue-500 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Orders ({orders.length})
        </button>
        <button
          onClick={() => setActiveTab("pricing")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === "pricing" ? "border-blue-500 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Pricing & Costs
        </button>
        <button
          onClick={() => setActiveTab("settings")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === "settings" ? "border-blue-500 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid="settings-tab"
        >
          Settings
        </button>
      </div>

      {/* Pipeline Tab - Drag and Drop Kanban Style */}
      {activeTab === "pipeline" && (
        <div className="space-y-6">
          {/* Orders Needing Work Orders */}
          {ordersNeedingWO.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <AlertCircle className="w-5 h-5" />
                  Orders Awaiting Work Order ({ordersNeedingWO.length})
                </CardTitle>
                <CardDescription>Create work orders to dispatch AB Labs for sample collection</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {ordersNeedingWO.map((order) => (
                    <div key={order.id} className="bg-white border border-amber-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-mono text-sm font-medium">{order.id}</span>
                        <Badge className="bg-amber-100 text-amber-700">{order.status}</Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-1">{order.facility_name || order.location_name}</p>
                      <p className="text-xs text-slate-400">{order.tests?.length || 0} tests · {order.sample_points?.length || 0} sample points</p>
                      <div className="mt-3 flex justify-between items-center">
                        <span className="font-medium text-green-600">${order.total_hospital_price?.toFixed(2) || order.total?.toFixed(2)}</span>
                        <Button 
                          size="sm" 
                          onClick={() => openCreateWODialog(order)}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`create-wo-${order.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Create WO
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pipeline Columns */}
          <div className="grid grid-cols-6 gap-4 overflow-x-auto pb-4">
            {STAGES.map((stage) => {
              const StageIcon = stage.icon;
              const stageWOs = workOrdersByStage[stage.id] || [];
              return (
                <div key={stage.id} className="min-w-[200px]">
                  <div className={`${stage.color} text-white px-3 py-2 rounded-t-lg flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <StageIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">{stage.label}</span>
                    </div>
                    <span className="bg-white/20 px-2 py-0.5 rounded text-xs">{stageWOs.length}</span>
                  </div>
                  <div className="bg-slate-50 border border-t-0 border-slate-200 rounded-b-lg p-2 min-h-[300px] space-y-2">
                    {stageWOs.map((wo) => (
                      <div 
                        key={wo.id} 
                        className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => setSelectedWO(wo)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-mono text-xs font-medium text-slate-600">{wo.id}</span>
                        </div>
                        <p className="text-sm font-medium text-slate-800 mb-1">{wo.facility_name || wo.location_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                          <Calendar className="w-3 h-3" />
                          {wo.scheduled_date} {wo.scheduled_time && `at ${wo.scheduled_time}`}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <MapPin className="w-3 h-3" />
                          {wo.sample_points?.length || 0} sample points
                        </div>
                        
                        {/* Stage-specific actions */}
                        <div className="mt-3 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
                          {stage.id === "intake" && (
                            <Button size="sm" variant="ghost" className="text-xs h-7" onClick={(e) => { e.stopPropagation(); openStageDialog(wo, "triage"); }}>
                              Move to Triage
                            </Button>
                          )}
                          {stage.id === "triage" && (
                            <Button size="sm" variant="ghost" className="text-xs h-7" onClick={(e) => { e.stopPropagation(); openStageDialog(wo, "dispatch"); }}>
                              Dispatch
                            </Button>
                          )}
                          {stage.id === "dispatch" && (
                            <Button size="sm" variant="ghost" className="text-xs h-7" onClick={(e) => { e.stopPropagation(); markSamplesCollected(wo.id); }}>
                              Samples Collected
                            </Button>
                          )}
                          {stage.id === "in_progress" && !wo.results_ready && (
                            <Button size="sm" variant="ghost" className="text-xs h-7" onClick={(e) => { e.stopPropagation(); markResultsReady(wo.id); }}>
                              Results Ready
                            </Button>
                          )}
                          {stage.id === "completion" && (
                            <Button size="sm" variant="ghost" className="text-xs h-7 text-green-600" onClick={(e) => { e.stopPropagation(); completeWorkOrder(wo.id); }}>
                              Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {stageWOs.length === 0 && (
                      <div className="text-center py-8 text-slate-400 text-xs">
                        No work orders
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === "orders" && (
        <Card>
          <CardHeader>
            <CardTitle>Water Testing Orders</CardTitle>
            <CardDescription>Manage orders from hospitals</CardDescription>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No orders yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Tests</TableHead>
                    <TableHead>Hospital Price</TableHead>
                    <TableHead>Vendor Cost</TableHead>
                    <TableHead>Margin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-mono text-sm">{order.id}</TableCell>
                      <TableCell>{order.location_name}</TableCell>
                      <TableCell>{order.tests?.length || 0} tests</TableCell>
                      <TableCell className="font-medium text-green-600">
                        ${order.total_hospital_price?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        ${order.total_vendor_cost?.toFixed(2)}
                      </TableCell>
                      <TableCell className="font-medium text-amber-600">
                        ${order.margin?.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          order.status === 'completed' ? 'bg-green-100 text-green-700' :
                          order.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          order.status === 'results_ready' ? 'bg-purple-100 text-purple-700' :
                          order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {order.status?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="pending">Pending</option>
                          <option value="sample_kits_sent">Sample Kits Sent</option>
                          <option value="samples_received">Samples Received</option>
                          <option value="in_progress">In Progress</option>
                          <option value="results_ready">Results Ready</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pricing Tab */}
      {activeTab === "pricing" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-blue-500" />
                Test Pricing ({catalog.markup_percentage || 44}% Markup)
              </CardTitle>
              <CardDescription>
                Vendor costs from AB Labs vs. hospital pricing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Test Name</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Vendor Cost</TableHead>
                    <TableHead className="text-right">Hospital Price</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catalog.tests?.map((test) => (
                    <TableRow key={test.id}>
                      <TableCell className="font-medium">{test.name}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{test.method}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          test.category === 'chemical' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                        }>
                          {test.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-slate-500">${test.vendor_cost?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">${test.price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-amber-600">${test.margin?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Fees</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fee</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Vendor Cost</TableHead>
                    <TableHead className="text-right">Hospital Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catalog.fees?.map((fee) => (
                    <TableRow key={fee.id}>
                      <TableCell className="font-medium">{fee.name}</TableCell>
                      <TableCell className="text-slate-500">{fee.description}</TableCell>
                      <TableCell className="text-right text-slate-500">${fee.vendor_cost?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">${fee.price?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Lab Partner: AB Labs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Address</p>
                  <p className="font-medium">5445 Almeda Road, Houston, TX 77004</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">(713) 863-9790</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">sterilemate@ablabs.com</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Certifications</p>
                  <div className="flex gap-2 mt-1">
                    <Badge variant="outline">NELAP</Badge>
                    <Badge variant="outline">TNI</Badge>
                    <Badge variant="outline">A2LA</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="space-y-6">
          <Card data-testid="markup-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-violet-500" />
                Pricing Settings
              </CardTitle>
              <CardDescription>
                Configure markup percentage and minimum invoice amounts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Markup Percentage Setting */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-medium">Markup Percentage</h3>
                    <p className="text-sm text-slate-500">
                      Applied to all vendor costs to calculate hospital prices
                    </p>
                  </div>
                  {!editingMarkup ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNewMarkup(catalog.markup_percentage || 44);
                        setEditingMarkup(true);
                      }}
                      data-testid="edit-markup-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingMarkup(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveMarkup}
                        disabled={savingMarkup}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="save-markup-btn"
                      >
                        {savingMarkup ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Save
                      </Button>
                    </div>
                  )}
                </div>
                
                {editingMarkup ? (
                  <div className="flex items-center gap-3 mt-4">
                    <Input
                      type="number"
                      value={newMarkup}
                      onChange={(e) => setNewMarkup(e.target.value)}
                      className="w-32"
                      min="0"
                      max="500"
                      step="0.5"
                      data-testid="markup-input"
                    />
                    <span className="text-slate-500">%</span>
                    <div className="text-sm text-slate-500 ml-4">
                      Example: ${13.00} vendor cost → ${(13 * (1 + parseFloat(newMarkup || 0) / 100)).toFixed(2)} hospital price
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-3xl font-bold text-violet-600" data-testid="current-markup">
                      {catalog.markup_percentage || 44}%
                    </span>
                    <span className="text-slate-400">markup on all tests</span>
                  </div>
                )}
              </div>

              {/* Minimum Invoice Display */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-1">Minimum Invoice</h3>
                <p className="text-sm text-slate-500 mb-3">
                  Vendor minimum: $200.00 → Hospital minimum: ${(200 * (1 + (catalog.markup_percentage || 44) / 100)).toFixed(2)}
                </p>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span className="text-2xl font-bold text-green-600">
                    {catalog.minimum_invoice_vendor ? 
                      `$${(catalog.minimum_invoice_vendor * (1 + (catalog.markup_percentage || 44) / 100)).toFixed(2)}` : 
                      `$${(200 * (1 + (catalog.markup_percentage || 44) / 100)).toFixed(2)}`}
                  </span>
                  <span className="text-slate-400">minimum order</span>
                </div>
              </div>

              {/* Info Card */}
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800">How Pricing Works</h4>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1">
                      <li>• Vendor costs are set by AB Labs in their quote</li>
                      <li>• Markup percentage is applied automatically to calculate hospital prices</li>
                      <li>• Changes to markup percentage apply to all tests and fees</li>
                      <li>• Hospital users only see the marked-up prices, never vendor costs</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Work Order Dialog */}
      <Dialog open={createWODialogOpen} onOpenChange={setCreateWODialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Create Work Order for AB Labs
            </DialogTitle>
            <DialogDescription>
              Schedule sample collection for order {selectedOrder?.id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedOrder && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800">{selectedOrder.facility_name || selectedOrder.location_name}</p>
                <p className="text-blue-600">{selectedOrder.tests?.length || 0} tests · {selectedOrder.sample_points?.length || 0} sample points</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scheduled_date">Scheduled Date *</Label>
                <Input
                  id="scheduled_date"
                  type="date"
                  value={woForm.scheduled_date}
                  onChange={(e) => setWoForm({...woForm, scheduled_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scheduled_time">Time</Label>
                <Input
                  id="scheduled_time"
                  type="text"
                  placeholder="e.g., 10:00 AM"
                  value={woForm.scheduled_time}
                  onChange={(e) => setWoForm({...woForm, scheduled_time: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name *</Label>
                <Input
                  id="contact_name"
                  value={woForm.contact_name}
                  onChange={(e) => setWoForm({...woForm, contact_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone *</Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={woForm.contact_phone}
                  onChange={(e) => setWoForm({...woForm, contact_phone: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="access_instructions">Access Instructions</Label>
              <Textarea
                id="access_instructions"
                placeholder="How should the technician access the building?"
                value={woForm.access_instructions}
                onChange={(e) => setWoForm({...woForm, access_instructions: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="special_requirements">Special Requirements</Label>
              <Textarea
                id="special_requirements"
                placeholder="Any special handling or requirements?"
                value={woForm.special_requirements}
                onChange={(e) => setWoForm({...woForm, special_requirements: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateWODialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={createWorkOrder} 
              disabled={creatingWO}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creatingWO ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Update Dialog */}
      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Move Work Order</DialogTitle>
            <DialogDescription>
              Move {selectedWO?.id} to {newStage?.replace("_", " ")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="stage_notes">Notes (optional)</Label>
              <Textarea
                id="stage_notes"
                placeholder="Add any notes about this stage transition..."
                value={stageNotes}
                onChange={(e) => setStageNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStageDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={updateWOStage} className="bg-blue-600 hover:bg-blue-700">
              <ChevronRight className="w-4 h-4 mr-2" />
              Move to {newStage?.replace("_", " ")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
